import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Settings2, ListChecks } from "lucide-react";
import { toast } from "sonner";

const BUILTIN_LABELS = {
  uniform_ok: "Uniform OK",
  toolkit_ok: "Toolkit OK",
  photos: "Required photos",
  completion_mode: "Road completion mode",
  length_km_cleaned: "Length / extent cleaned",
  completed_as_per_plan: "Completed as per plan",
  waste_removed: "Waste removed?",
};

const QUESTION_TYPES = [
  { v: "yes_no", label: "Yes / No" },
  { v: "short_text", label: "Short text" },
  { v: "number", label: "Number" },
  { v: "multi_choice", label: "Multiple choice" },
];

export default function CleaningQuestionsPage() {
  const [data, setData] = useState({ questions: [], builtins: { before: [], after: [] }, disabled_builtins: { before: [], after: [] } });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("before");
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm("before"));
  const [saving, setSaving] = useState(false);

  function initialForm(kind) {
    return {
      kind,
      label: "",
      question_type: "yes_no",
      required: false,
      order: 0,
      active: true,
      options: [],
      help_text: "",
      reason_when_value: "",
      reason_label: "",
    };
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/cleaning-questions");
      setData(r.data || { questions: [], builtins: {}, disabled_builtins: {} });
    } catch {
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const questionsByKind = useMemo(() => {
    const out = { before: [], after: [] };
    (data.questions || []).forEach((q) => {
      if (out[q.kind]) out[q.kind].push(q);
    });
    out.before.sort((a, b) => (a.order || 0) - (b.order || 0));
    out.after.sort((a, b) => (a.order || 0) - (b.order || 0));
    return out;
  }, [data]);

  const openNew = (kind) => {
    setEditing(null);
    setForm({ ...initialForm(kind), order: (questionsByKind[kind]?.length || 0) + 1 });
    setDlgOpen(true);
  };

  const openEdit = (q) => {
    setEditing(q);
    setForm({
      kind: q.kind,
      label: q.label || "",
      question_type: q.question_type || "yes_no",
      required: !!q.required,
      order: q.order || 0,
      active: q.active !== false,
      options: q.options || [],
      help_text: q.help_text || "",
      reason_when_value: q.reason_when_value || "",
      reason_label: q.reason_label || "",
    });
    setDlgOpen(true);
  };

  const save = async () => {
    if (!form.label.trim()) return toast.error("Question label is required");
    if (form.question_type === "multi_choice" && !(form.options || []).length) {
      return toast.error("Add at least one option for multiple-choice");
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        label: form.label.trim(),
        help_text: form.help_text?.trim() || null,
        reason_when_value: form.reason_when_value?.trim() || null,
        reason_label: form.reason_label?.trim() || null,
        options: (form.options || []).filter((o) => o.trim()),
      };
      if (editing) {
        await api.patch(`/admin/cleaning-questions/${editing.id}`, payload);
        toast.success("Question updated");
      } else {
        await api.post("/admin/cleaning-questions", payload);
        toast.success("Question added");
      }
      setDlgOpen(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete question "${q.label}"?`)) return;
    try {
      await api.delete(`/admin/cleaning-questions/${q.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const move = async (q, delta) => {
    const list = questionsByKind[q.kind];
    const idx = list.findIndex((x) => x.id === q.id);
    const swapWith = list[idx + delta];
    if (!swapWith) return;
    try {
      await Promise.all([
        api.patch(`/admin/cleaning-questions/${q.id}`, { order: swapWith.order || idx }),
        api.patch(`/admin/cleaning-questions/${swapWith.id}`, { order: q.order || (idx + delta) }),
      ]);
      load();
    } catch {
      toast.error("Reorder failed");
    }
  };

  const toggleBuiltin = async (kind, key, disable) => {
    const cur = new Set(data.disabled_builtins?.[kind] || []);
    if (disable) cur.add(key); else cur.delete(key);
    try {
      await api.put("/admin/cleaning-questions/builtins", { kind, disabled_keys: [...cur] });
      toast.success("Updated");
      load();
    } catch {
      toast.error("Failed to update built-in");
    }
  };

  return (
    <AppShell title="Cleaning Questionnaire CMS" subtitle="Customize Before / After cleaning checklists">
      <div className="flex flex-col gap-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="before" data-testid="cq-tab-before">Before-cleaning</TabsTrigger>
            <TabsTrigger value="after" data-testid="cq-tab-after">After-cleaning</TabsTrigger>
          </TabsList>

          {["before", "after"].map((kind) => (
            <TabsContent key={kind} value={kind} className="space-y-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Settings2 className="h-4 w-4 text-primary" /> Built-in fields
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {(data.builtins?.[kind] || []).map((k) => {
                      const disabled = (data.disabled_builtins?.[kind] || []).includes(k);
                      return (
                        <div key={k} className="flex items-center justify-between rounded-md border p-3 text-sm">
                          <div>
                            <div className="font-medium">{BUILTIN_LABELS[k] || k}</div>
                            <div className="text-[11px] text-slate-500">key: {k}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs ${disabled ? "text-slate-400" : "text-emerald-700"}`}>
                              {disabled ? "Disabled" : "Enabled"}
                            </span>
                            <Switch
                              checked={!disabled}
                              onCheckedChange={(v) => toggleBuiltin(kind, k, !v)}
                              data-testid={`cq-builtin-${kind}-${k}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <ListChecks className="h-4 w-4 text-primary" /> Custom questions
                  </CardTitle>
                  <Button size="sm" onClick={() => openNew(kind)} data-testid={`cq-add-${kind}`}>
                    <Plus className="mr-1.5 h-4 w-4" /> Add question
                  </Button>
                </CardHeader>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="p-6 text-sm text-muted-foreground">Loading…</div>
                  ) : (questionsByKind[kind] || []).length === 0 ? (
                    <div className="p-6 text-sm text-muted-foreground">No custom questions yet.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-4 py-2 text-left">Order</th>
                            <th className="px-4 py-2 text-left">Question</th>
                            <th className="px-4 py-2 text-left">Type</th>
                            <th className="px-4 py-2 text-left">Required</th>
                            <th className="px-4 py-2 text-left">Active</th>
                            <th className="px-4 py-2 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {questionsByKind[kind].map((q, i) => (
                            <tr key={q.id} className="border-t" data-testid={`cq-row-${q.id}`}>
                              <td className="px-4 py-2 text-slate-500">{q.order || i + 1}</td>
                              <td className="px-4 py-2 font-medium text-slate-700">
                                {q.label}
                                {q.help_text && <div className="text-[11px] text-slate-500">{q.help_text}</div>}
                              </td>
                              <td className="px-4 py-2">
                                <Badge variant="secondary">{QUESTION_TYPES.find((t) => t.v === q.question_type)?.label || q.question_type}</Badge>
                              </td>
                              <td className="px-4 py-2">{q.required ? "Yes" : "No"}</td>
                              <td className="px-4 py-2">
                                <Switch
                                  checked={q.active !== false}
                                  onCheckedChange={async (v) => {
                                    try {
                                      await api.patch(`/admin/cleaning-questions/${q.id}`, { active: v });
                                      load();
                                    } catch { toast.error("Update failed"); }
                                  }}
                                  data-testid={`cq-active-${q.id}`}
                                />
                              </td>
                              <td className="px-4 py-2 text-right">
                                <div className="inline-flex items-center gap-1">
                                  <Button size="icon" variant="ghost" onClick={() => move(q, -1)} disabled={i === 0} data-testid={`cq-up-${q.id}`}>
                                    <ArrowUp className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button size="icon" variant="ghost" onClick={() => move(q, 1)} disabled={i === questionsByKind[kind].length - 1} data-testid={`cq-down-${q.id}`}>
                                    <ArrowDown className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button size="icon" variant="ghost" onClick={() => openEdit(q)} data-testid={`cq-edit-${q.id}`}>
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button size="icon" variant="ghost" onClick={() => remove(q)} data-testid={`cq-delete-${q.id}`}>
                                    <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>

        <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="cq-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit question" : "New question"}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Kind</label>
                <div className="mt-1 inline-flex rounded-md border text-xs overflow-hidden">
                  {["before", "after"].map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => !editing && setForm({ ...form, kind: k })}
                      disabled={!!editing}
                      className={`px-3 py-1.5 ${form.kind === k ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary"}`}
                    >
                      {k}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Question label *</label>
                <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="cq-label" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Type</label>
                <select
                  className="mt-1 w-full rounded-md border px-3 py-2 text-sm bg-background"
                  value={form.question_type}
                  onChange={(e) => setForm({ ...form, question_type: e.target.value })}
                  data-testid="cq-type"
                >
                  {QUESTION_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
              </div>
              {form.question_type === "multi_choice" && (
                <div>
                  <label className="text-xs font-medium text-slate-600">Options (one per line)</label>
                  <Textarea
                    value={(form.options || []).join("\n")}
                    onChange={(e) => setForm({ ...form, options: e.target.value.split("\n") })}
                    rows={4}
                  />
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-slate-600">Help text (optional)</label>
                <Input value={form.help_text} onChange={(e) => setForm({ ...form, help_text: e.target.value })} />
              </div>
              {form.question_type === "yes_no" && (
                <div className="rounded-md border p-3">
                  <div className="text-xs font-medium text-slate-600">Follow-up reason when answer equals…</div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <div>
                      <label className="text-[10px] uppercase text-slate-400">Trigger value</label>
                      <select
                        className="mt-1 w-full rounded-md border px-3 py-2 text-sm bg-background"
                        value={form.reason_when_value || ""}
                        onChange={(e) => setForm({ ...form, reason_when_value: e.target.value })}
                      >
                        <option value="">— None —</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-slate-400">Reason label</label>
                      <Input value={form.reason_label || ""} onChange={(e) => setForm({ ...form, reason_label: e.target.value })} placeholder="Explain why..." />
                    </div>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between rounded-md border p-3">
                <div className="text-sm">Required</div>
                <Switch checked={form.required} onCheckedChange={(v) => setForm({ ...form, required: v })} />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div className="text-sm">Active</div>
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDlgOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="cq-save">{saving ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
